import { Screen } from '../../components';
import { PROTECTED_ROUTES } from '../../constants/routes';
import { useUser } from '../../hooks/useUser';
import { Link } from 'react-router-dom';
import {
    IconGitBranch,
    IconBook,
    IconFolder,
    IconHelp,
    IconCalendarEvent,
    IconSchool,
    IconUsers,
    IconCreditCard,
    IconFileText,
    IconScale
} from '@tabler/icons-react';
import clsx from 'clsx';
import './AdminPage.scss';

interface AdminCardProps {
    title: string
    description: string
    link: string
    icon: React.ReactNode
    gradient: string
}

const AdminCard: React.FC<AdminCardProps> = ({ title, description, link, icon, gradient }) => {
    return (
        <Link
            to={link}
            className={clsx(
                'group relative rounded-xl border-2 transition-all duration-300 overflow-hidden',
                'bg-white border-gray-200 shadow-md',
                'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
                'hover:shadow-xl dark:hover:shadow-2xl',
                'hover:scale-[1.02] active:scale-[0.98]',
                'flex flex-col'
            )}
        >
            <div className={clsx(
                'absolute top-0 left-0 right-0 h-1',
                gradient
            )} />
            <div className="p-6 flex-1 flex flex-col">
                <div className={clsx(
                    'w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform duration-300',
                    'group-hover:scale-110',
                    gradient
                )}>
                    {icon}
                </div>
                <h3 className={clsx(
                    'text-xl font-bold mb-2 transition-colors duration-300',
                    'text-gray-900 dark:text-white'
                )}>
                    {title}
                </h3>
                <p className={clsx(
                    'text-sm leading-relaxed flex-1 transition-colors duration-300',
                    'text-gray-600 dark:text-gray-400'
                )}>
                    {description}
                </p>
                <div className="mt-4 flex items-center gap-2">
                    <span className={clsx(
                        'text-sm font-semibold transition-colors duration-300',
                        'text-blue-600 dark:text-blue-400',
                        'group-hover:text-blue-700 dark:group-hover:text-blue-300'
                    )}>
                        Administrar
                    </span>
                    <svg
                        className={clsx(
                            'w-4 h-4 transition-transform duration-300',
                            'text-blue-600 dark:text-blue-400',
                            'group-hover:translate-x-1'
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>
        </Link>
    );
};

const AdminPage = () => {
    const { isAdmin } = useUser();

    const adminSections = [
        {
            title: 'Ramas',
            description: 'Administración de ramas. Cada rama es individual y contiene sus propios bloques.',
            link: PROTECTED_ROUTES.ADMIN_BRANCHES,
            icon: <IconGitBranch size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-blue-500 to-cyan-500',
            show: isAdmin
        },
        {
            title: 'Bloques',
            description: 'Administración de bloques. Cada bloque es individual y contiene sus propios temas.',
            link: PROTECTED_ROUTES.ADMIN_SUBJECTS,
            icon: <IconBook size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-purple-500 to-pink-500',
            show: isAdmin
        },
        {
            title: 'Temas',
            description: 'Administración de temas. Contiene sus propias preguntas.',
            link: PROTECTED_ROUTES.ADMIN_TOPICS,
            icon: <IconFolder size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-green-500 to-emerald-500',
            show: true
        },
        {
            title: 'Preguntas',
            description: 'Administración de preguntas. Créalas para asignarlas después a tests. También podrán salir en tests aleatorios.',
            link: PROTECTED_ROUTES.ADMIN_QUESTIONS,
            icon: <IconHelp size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-orange-500 to-red-500',
            show: true
        },
        {
            title: 'Eventos',
            description: 'Administración de eventos',
            link: PROTECTED_ROUTES.ADMIN_SESSIONS,
            icon: <IconCalendarEvent size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-indigo-500 to-purple-500',
            show: true
        },
        {
            title: 'Maestros',
            description: 'Administración de maestros. Permite asignarlos a clases para que los alumnos sepan quién la impartirá.',
            link: PROTECTED_ROUTES.ADMIN_TEACHERS,
            icon: <IconSchool size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-teal-500 to-cyan-500',
            show: isAdmin
        },
        {
            title: 'Usuarios',
            description: 'Administración de usuarios. Son los que consumirán el contenido de la plataforma.',
            link: PROTECTED_ROUTES.USERS,
            icon: <IconUsers size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-violet-500 to-purple-500',
            show: isAdmin
        },
        {
            title: 'Suscripciones',
            description: 'Administración de suscripciones. Dale acceso al contenido a tus alumnos.',
            link: PROTECTED_ROUTES.ADMIN_SUSCRIPTIONS,
            icon: <IconCreditCard size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-amber-500 to-orange-500',
            show: isAdmin
        },
        {
            title: 'Tests preparados',
            description: 'Aquí podrás preparar tests para que los hagan tus alumnos.',
            link: PROTECTED_ROUTES.ADMIN_TESTS,
            icon: <IconFileText size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-rose-500 to-pink-500',
            show: true
        },
        {
            title: 'Bases de la oposición',
            description: 'Sube y gestiona las bases de la oposición en formato PDF.',
            link: PROTECTED_ROUTES.ADMIN_OPPOSITION_BASES,
            icon: <IconScale size={24} className="text-white" />,
            gradient: 'bg-gradient-to-br from-slate-500 to-gray-600',
            show: isAdmin
        }
    ];

    const visibleSections = adminSections.filter(section => section.show);

    return (
        <Screen id="admin-page" title="Administración">
            <div className="w-full -mx-8 sm:-mx-6 md:mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 p-4 sm:p-6 md:p-0">
                    {visibleSections.map((section) => (
                        <AdminCard
                            key={section.link}
                            title={section.title}
                            description={section.description}
                            link={section.link}
                            icon={section.icon}
                            gradient={section.gradient}
                        />
                    ))}
                </div>
            </div>
        </Screen>
    );
};

export { AdminPage };
